/**
 * AP-39 Nachtrag 1 — **die Positionen des zugeordneten Belegs**, neben dem Original (Auftraggeber 13.10.2026):
 * „Ich schreibe eine Preisanfrage, bekomme das Angebot zurück — die KI findet die Anfrage, schreibt mir ihre
 * Positionen hin und setzt den Preis des Lieferanten ein, markiert, damit ich erkenne, was sie eingetragen hat."
 *
 * Je Zeile des Belegs, was der Lieferant dazu schreibt. **Violett** ist gelesen und gilt noch nicht; erst
 * „Übernehmen" macht daraus die Antwort am Beleg — mit den Rechten des Belegs. Gekennzeichnet wird, wo das
 * Dokument vom Beleg abweicht: nicht angeboten, Alternative, zusätzlich, fehlt, weicht von der Bestellung ab.
 * Sinngemäß dasselbe für die Auftragsbestätigung an der Bestellung.
 *
 * **Dasselbe Vorschlagsfenster wie Eingangsrechnung und Abgabezettel** (`components/ki-vorschlag`, AP-22 Teil 6;
 * Auftraggeber 14.10.2026): Rahmen, Zustände, Tabelle, Auswahl, Entscheidung je Zeile und Fußzeile kommen von dort.
 * Ist nichts gelesen, stehen die Positionen in einem gewöhnlichen Abschnitt — ohne die Farbe des Vorschlags.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { geldText, preisText, type InboxLineMark, type InboxLineValues, type InboxValuesLine, type InboxValuesView } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { fmtDate } from '@/lib/format-date'
import { Abschnitt } from '@/lib/speichern'
import { KiAuswahl, KiEinleitung, KiEntscheidungZelle, KiFuss, KiLesenKnopf, KiLiest, KiTabelle, KiVorschlag, kiGelb, kiTd, kiTh } from '@/components/ki-vorschlag/KiVorschlag'
import { datumSchreibweisen, kern, type Fundstelle } from '@/components/original/fundstellen'
import { confirmInboxValues, discardInboxValues } from '../api/inbox.api'

/** gelesen, noch nicht übernommen — in der Farbe des Vorschlags */
const violett = 'bg-violet-500/15'
const gewaehltKlasse = 'outline outline-2 -outline-offset-2 outline-violet-500/70'

const mengeText = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })
/** Dieselbe Bezeichnung, auch wenn der Lieferant „100x100" schreibt und der Beleg „100×100". */
const gleicherText = (a: string, b: string) => kern(a.replace(/×/g, 'x')) === kern(b.replace(/×/g, 'x'))

/** Welche Preisspalten die Tabelle braucht: Preiseinheit und Rabatt nur, wenn eine Zeile sie trägt — der Platz gehört der Bezeichnung. */
function spaltenVon(a: InboxValuesView) {
  const werte = a.lines.flatMap((z) => [z.read, z.values]).filter((w): w is InboxLineValues => !!w)
  const pe = a.prices && werte.some((w) => (w.priceUnit ?? 1) !== 1)
  const rabatt = a.prices && werte.some((w) => !!w.discountPercent)
  return { pe, rabatt, netto: a.kind === 'OFFER' && (pe || rabatt) }
}

/** Die Wörter je Art: Am Angebot heißt es „angeboten", an der Auftragsbestätigung „bestätigt". */
const WORT = {
  OFFER: { beleg: 'Preisanfrage', dokument: 'des Angebots', nummer: 'Angebotsnummer', datum: 'Angebot vom', fehlt: 'fehlt im Angebot', bearbeiten: 'Preisanfragen' },
  ORDER_CONFIRMATION: { beleg: 'Bestellung', dokument: 'der Auftragsbestätigung', nummer: 'AB-Nummer', datum: 'Bestätigung vom', fehlt: 'nicht bestätigt', bearbeiten: 'Bestellungen' },
} as const

const MARKE_KLASSE: Record<InboxLineMark, string> = {
  NOT_OFFERED: 'bg-destructive/15 text-destructive',
  ALTERNATIVE: 'bg-sky-500/15 text-sky-800 dark:text-sky-200',
  EXTRA: 'bg-indigo-500/15 text-indigo-800 dark:text-indigo-200',
  MISSING: 'bg-amber-500/20 text-amber-800 dark:text-amber-200',
  DEVIATION: 'bg-amber-500/20 text-amber-800 dark:text-amber-200',
}

/** Kennung einer Fundstelle — dieselbe an der Zeile und an ihrer Markierung im Original. */
export const stelleWert = (ziel: string, key: string) => `wert:${ziel}:${key}`

/** Was im Original zu markieren ist: je Zeile ihr Zitat bei ihrer Bezeichnung, dazu Nummer und Datum des Dokuments. */
export function werteFundstellen(a: InboxValuesView): Fundstelle[] {
  const kopf = (key: 'number' | 'date' | 'validUntil') => a.head.find((f) => f.key === key)
  const nummer = kopf('number')?.read ?? kopf('number')?.value
  const datum = kopf('date')?.read ?? kopf('date')?.value
  const w = WORT[a.kind]
  return [
    ...(nummer ? [{ id: stelleWert(a.targetId, 'kopf:number'), texte: [nummer], titel: w.nummer }] : []),
    ...(datum ? [{ id: stelleWert(a.targetId, 'kopf:date'), texte: datumSchreibweisen(datum), titel: w.datum }] : []),
    ...a.lines
      .filter((z) => z.source && (z.source.quote || z.source.text))
      .map((z) => ({ id: stelleWert(a.targetId, z.key), seite: z.source!.page, texte: [z.source!.quote], anker: z.source!.text ?? z.name, titel: `${z.position ? `Pos. ${z.position}: ` : ''}${z.name}` })),
  ]
}

export function WertePositionen({
  itemId,
  ansicht,
  darf,
  lesen,
  liest,
  gewaehlt,
  onZeige,
  onAnsichten,
  onFehler,
}: {
  itemId: string
  ansicht: InboxValuesView
  /** `inbox.item.manage` — ohne es wird nur gezeigt */
  darf: boolean
  /** „Mit KI lesen" für dieses Ziel; fehlt, wenn die KI nicht gebucht, nicht eingeschaltet oder für den Eintrag gesperrt ist */
  lesen?: () => void
  liest: boolean
  /** Die Stelle, die das Original gerade zeigt */
  gewaehlt: string | null
  onZeige: (stelle: string, seite: number | null) => void
  /** Nach „Übernehmen" und „Verwerfen": der neue Stand aller Ziele des Eintrags */
  onAnsichten: (a: InboxValuesView[]) => void
  onFehler: (t: string | null) => void
}) {
  const w = WORT[ansicht.kind]
  const angebot = ansicht.kind === 'OFFER'
  const offene = useMemo(() => ansicht.lines.filter((z) => z.readId), [ansicht])
  const [haken, setHaken] = useState<Set<string>>(new Set())
  const [kopfHaken, setKopfHaken] = useState(true)
  const [laeuft, setLaeuft] = useState(false)
  // Vorauswahl je Lesung: alles außer „niedrig" (wie am Beleg) — neu, sobald andere Zeilen offen sind.
  const kennung = offene.map((z) => z.readId).join(',')
  useEffect(() => {
    setHaken(new Set(offene.filter((z) => z.read?.confidence !== 'niedrig').map((z) => z.readId!)))
    setKopfHaken(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kennung])

  const tue = async (was: typeof confirmInboxValues, ids: string[], head: boolean, text: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      onAnsichten(await was(itemId, { targetKind: ansicht.targetKind, targetId: ansicht.targetId, ids, head }))
    } catch (e) {
      onFehler(apiErrorText(e, text))
    } finally {
      setLaeuft(false)
    }
  }
  const schalte = (id: string) =>
    setHaken((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  const offen = ansicht.open > 0 || ansicht.headOpen
  const kann = darf && ansicht.canConfirm
  const zusaetzlich = ansicht.lines.filter((z) => !z.lineId)
  const spalten = spaltenVon(ansicht)
  const stelle = (key: string) => stelleWert(ansicht.targetId, key)
  const kopfText = (key: 'number' | 'date' | 'validUntil') => (key === 'number' ? w.nummer : key === 'date' ? w.datum : 'gültig bis')
  const kopfWert = (key: string, wert: string | null) => (wert && key !== 'number' ? fmtDate(wert) : (wert ?? ''))
  const titel = `Positionen der ${w.beleg}`
  const verweis = ansicht.url ? (
    <Link to={ansicht.url} className="inline-flex min-w-0 items-center gap-1 text-sm text-primary hover:underline" title={`${w.beleg} öffnen`}>
      <span className="truncate">{ansicht.label}</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
    </Link>
  ) : (
    <span className="truncate text-sm text-muted-foreground">{ansicht.label}</span>
  )
  const lesenKnopf = lesen ? <KiLesenKnopf erneut={!!ansicht.readFrom || ansicht.lines.some((z) => z.source)} disabled={liest || laeuft} onClick={lesen} /> : undefined

  const inhalt = (
    <>
      {liest && <KiLiest text={`Die KI liest die Werte ${w.dokument}`}>Das dauert meist unter einer Minute.</KiLiest>}
      {ansicht.open > 0 && ansicht.prices && (
        <KiEinleitung zusatz={ansicht.readFrom?.fileName ? `Gelesen aus „${ansicht.readFrom.fileName}".` : undefined}>
          <span className={`rounded px-1 ${violett}`}>Violett</span> hat die KI aus dem Dokument gelesen — es gilt erst nach „Übernehmen". <span className={`rounded px-1 ${kiGelb}`}>Gelb</span> heißt: nicht sicher gelesen. Ein Klick auf eine Zeile zeigt die Stelle im Original.
        </KiEinleitung>
      )}
      {/* Was nicht zu sehen ist, wird erklärt, nicht verschwiegen: Gelesenes sind Preise. */}
      {ansicht.blocked === 'PRICE_RIGHT' && (
        <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
          {ansicht.open > 0 ? `${ansicht.open} ${ansicht.open === 1 ? 'Zeile ist' : 'Zeilen sind'} gelesen. ` : ''}Die gelesenen Werte sind Preise — sie sieht und übernimmt, wer im Belegwesen Preise sehen darf.
        </p>
      )}
      {ansicht.blocked === 'EDIT_RIGHT' && offen && <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">Zum Übernehmen fehlt dir das Recht, {w.bearbeiten} zu bearbeiten.</p>}
      {ansicht.blocked === 'CLOSED' && <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">Die {w.beleg} ist storniert oder noch vorläufig — hier lässt sich nichts übernehmen.</p>}

      {(ansicht.headOpen || ansicht.head.some((f) => f.value)) && (
        <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          {kann && ansicht.headOpen && <input type="checkbox" aria-label={`Kopf ${w.dokument} übernehmen`} checked={kopfHaken} onChange={(e) => setKopfHaken(e.target.checked)} />}
          {ansicht.head.map((f) => {
            const gelesen = f.read !== null && f.read !== f.value
            if (!gelesen && !f.value) return null
            const id = stelle(`kopf:${f.key}`)
            return (
              <div key={f.key} data-fundstelle={id} className={`flex cursor-pointer items-baseline gap-2 rounded px-1 ${gewaehlt === id ? gewaehltKlasse : ''}`} onClick={() => onZeige(id, null)}>
                <dt className="text-muted-foreground">{kopfText(f.key)}</dt>
                <dd className={gelesen ? `rounded px-1 font-medium ${violett}` : 'font-medium'}>{kopfWert(f.key, gelesen ? f.read : f.value)}</dd>
                {gelesen && f.value && <dd className="text-xs text-muted-foreground line-through">{kopfWert(f.key, f.value)}</dd>}
              </div>
            )
          })}
        </dl>
      )}

      {ansicht.lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">Die {w.beleg} hat keine Positionen.</p>
      ) : (
        <KiTabelle
          kopf={
            <>
              <th className="w-8 px-2 py-1.5" />
              <th className="px-2 py-1.5 font-medium">Pos.</th>
              <th className={`min-w-[13rem] ${kiTh}`}>Bezeichnung</th>
              <th className={`${kiTh} text-right`}>{angebot ? 'Menge' : 'bestellt'}</th>
              {!angebot && <th className={`${kiTh} text-right`}>bestätigt</th>}
              {ansicht.prices && (
                <>
                  <th className={`${kiTh} text-right`}>Einzelpreis</th>
                  {spalten.pe && (
                    <th className={`${kiTh} text-right`} title="Preiseinheit">
                      PE
                    </th>
                  )}
                  {spalten.rabatt && <th className={`${kiTh} text-right`}>Rabatt %</th>}
                  {spalten.netto && <th className={`${kiTh} text-right`}>Netto je Einheit</th>}
                </>
              )}
              <th className={`${kiTh} text-right`}>{angebot ? 'Lieferzeit' : 'Liefertermin'}</th>
              <th className={kiTd} />
            </>
          }
        >
          {ansicht.lines.map((z) => (
            <Zeile
              key={z.key}
              z={z}
              ansicht={ansicht}
              spalten={spalten}
              stelle={stelle(z.key)}
              gewaehlt={gewaehlt === stelle(z.key)}
              ersteZusaetzliche={zusaetzlich[0]?.key === z.key}
              kann={kann}
              laeuft={laeuft}
              haken={!!z.readId && haken.has(z.readId)}
              onHaken={() => z.readId && schalte(z.readId)}
              onZeige={onZeige}
              onVerwerfen={() => z.readId && void tue(discardInboxValues, [z.readId], false, 'Die Zeile ließ sich nicht verwerfen.')}
            />
          ))}
        </KiTabelle>
      )}
      {ansicht.prices && ansicht.total ? (
        <p className="text-right text-sm text-muted-foreground">
          Summe netto der übernommenen Werte: <span className="font-medium tabular-nums text-foreground">{geldText(ansicht.total)}</span>
        </p>
      ) : null}

      {kann && offen ? (
        <KiFuss
          anzahl={haken.size + (kopfHaken && ansicht.headOpen ? 1 : 0)}
          laeuft={laeuft}
          onUebernehmen={() => void tue(confirmInboxValues, [...haken], kopfHaken && ansicht.headOpen, 'Die Werte ließen sich nicht übernehmen.')}
          onSichere={() => setHaken(new Set(offene.filter((z) => z.read?.confidence !== 'niedrig').map((z) => z.readId!)))}
          onRestVerwerfen={() =>
            void tue(
              discardInboxValues,
              offene.map((z) => z.readId!),
              true,
              'Der Rest ließ sich nicht verwerfen.',
            )
          }
          lesen={lesenKnopf}
        />
      ) : (
        lesenKnopf && <div className="flex">{lesenKnopf}</div>
      )}
    </>
  )

  // Gelesen oder liest die KI gerade: das Vorschlagsfenster. Sonst nur die Positionen mit dem, was am Beleg gilt.
  return offen || liest ? (
    <KiVorschlag titel={titel} marke={ansicht.open > 0 ? `${ansicht.open} gelesen — ${ansicht.open === 1 ? 'gilt' : 'gelten'} noch nicht` : undefined} aktionen={verweis}>
      {ansicht.stateText && <StandText>{ansicht.stateText}</StandText>}
      {inhalt}
    </KiVorschlag>
  ) : (
    <Abschnitt titel={titel} aktionen={verweis}>
      <div className="space-y-3">
        {ansicht.stateText && <StandText>{ansicht.stateText}</StandText>}
        {inhalt}
      </div>
    </Abschnitt>
  )
}

/** Bestellung: der Stand der Bestätigung am Beleg („teilweise bestätigt — Termin +3 Tage"). */
function StandText({ children }: { children: ReactNode }) {
  return <p className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{children}</p>
}

/** Ein Wert der Zeile: das Gelesene hervorgehoben, darunter klein, was bisher galt — sonst, was gilt. */
function Wert({ z, feld, text, unsicher }: { z: InboxValuesLine; feld: keyof InboxLineValues; text: (w: number | string) => string; unsicher: boolean }) {
  const gilt = z.values?.[feld] ?? null
  const liest = z.read ? (z.read[feld] ?? null) : null
  const zeige = (w: InboxLineValues[keyof InboxLineValues]) => (w === null || w === undefined || typeof w === 'boolean' ? '' : text(w))
  if (z.read && liest !== null && liest !== '') {
    return (
      <>
        <span className={`rounded px-1 font-medium ${unsicher ? kiGelb : violett}`}>{zeige(liest)}</span>
        {gilt !== null && gilt !== liest && zeige(gilt) && <span className="block text-xs text-muted-foreground line-through">{zeige(gilt)}</span>}
      </>
    )
  }
  // Wird gerade über Gelesenes entschieden, gilt der alte Wert nur noch bis dahin — er steht gedämpft da.
  return <span className={z.read ? 'text-muted-foreground' : ''}>{zeige(gilt)}</span>
}

function Zeile({
  z,
  ansicht,
  spalten,
  stelle,
  gewaehlt,
  ersteZusaetzliche,
  kann,
  laeuft,
  haken,
  onHaken,
  onZeige,
  onVerwerfen,
}: {
  z: InboxValuesLine
  ansicht: InboxValuesView
  spalten: ReturnType<typeof spaltenVon>
  stelle: string
  gewaehlt: boolean
  ersteZusaetzliche: boolean
  kann: boolean
  laeuft: boolean
  haken: boolean
  onHaken: () => void
  onZeige: (stelle: string, seite: number | null) => void
  onVerwerfen: () => void
}) {
  const w = WORT[ansicht.kind]
  const angebot = ansicht.kind === 'OFFER'
  const unsicher = !!z.read && z.read.confidence !== 'hoch'
  const nichtAngeboten = z.marks.includes('NOT_OFFERED')
  const alternative = z.read?.alternative ?? z.values?.alternative ?? null
  const artikel = z.read?.articleNumber ?? z.values?.articleNumber ?? null
  const einheit = z.read?.unitCode ?? z.values?.unitCode ?? null
  // Was die KI als Bezeichnung gelesen hat — nur zeigen, wenn es nicht ohnehin die der Zeile ist.
  const imDokument = z.source?.text && z.lineId && !alternative && !gleicherText(z.source.text, z.name) ? z.source.text : null
  const breite = 6 + (angebot ? 0 : 1) + (ansicht.prices ? 1 + Number(spalten.pe) + Number(spalten.rabatt) + Number(spalten.netto) : 0)
  const geld = (x: number | string) => preisText(Number(x))
  const zahl = (x: number | string) => mengeText(Number(x))
  const gruende = [
    z.read?.quoteFound === false ? 'Zitat steht so nicht auf der Seite' : '',
    z.read?.unitHint ? `Preis je ${z.read.unitHint} — nicht je ${z.unitCode ?? 'Einheit'} der Zeile` : '',
    z.read && z.read.confidence && z.read.confidence !== 'hoch' ? `Sicherheit ${z.read.confidence}` : '',
  ].filter(Boolean)
  const markenText: Record<InboxLineMark, string> = { NOT_OFFERED: 'nicht angeboten', ALTERNATIVE: 'Alternative', EXTRA: 'zusätzlich', MISSING: w.fehlt, DEVIATION: 'weicht ab' }

  return (
    <>
      {ersteZusaetzliche && (
        <tr className="border-b border-border bg-muted/40">
          <td colSpan={breite} className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zusätzlich im Dokument — steht nicht in der {w.beleg}
          </td>
        </tr>
      )}
      <tr
        data-fundstelle={stelle}
        className={`cursor-pointer border-b border-border align-top last:border-0 ${z.read?.confidence === 'niedrig' ? kiGelb : ''} ${z.marks.includes('MISSING') ? 'text-muted-foreground' : ''} ${gewaehlt ? gewaehltKlasse : ''}`}
        onClick={() => onZeige(stelle, z.source?.page ?? null)}
      >
        <KiAuswahl label={`${z.name} übernehmen`} zeigen={!!z.readId && kann} checked={haken} onChange={onHaken} />
        <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-muted-foreground">{z.position ?? ''}</td>
        <td className={kiTd}>
          <span className={nichtAngeboten ? 'line-through decoration-destructive/60' : ''}>{z.name}</span>
          {z.articleNumber && <span className="ml-2 text-xs text-muted-foreground">{z.articleNumber}</span>}
          {/* Die Kennzeichen stehen bei der Bezeichnung — dort sucht das Auge, was mit der Position ist. */}
          {z.marks
            .filter((m) => m !== 'DEVIATION')
            .map((m) => (
              <span key={m} className={`ml-2 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${MARKE_KLASSE[m]}`}>
                {markenText[m]}
              </span>
            ))}
          {z.deviations.map((a) => (
            <span key={a} className={`ml-2 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${MARKE_KLASSE.DEVIATION}`}>
              {a}
            </span>
          ))}
          {imDokument && <span className="block text-xs italic text-muted-foreground">im Dokument: {imDokument}</span>}
          {alternative && (
            <span className="block text-xs">
              <span className={`rounded px-1 ${z.read ? violett : ''}`}>Alternative: {alternative}</span>
            </span>
          )}
          {artikel && artikel !== z.articleNumber && <span className="block text-xs text-muted-foreground">Art.-Nr. des Lieferanten: {artikel}</span>}
          {gruende.length > 0 && <span className="block text-xs text-amber-800 dark:text-amber-200">{gruende.join(' · ')}</span>}
        </td>
        <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>
          {/* Eine zusätzliche Zeile hat keine Menge des Belegs — ihre eigene steht dort, wo sonst die angefragte steht. */}
          {z.lineId || angebot ? (z.quantity !== null ? `${mengeText(z.quantity)} ${z.unitCode ?? ''}` : '') : ''}
        </td>
        {!angebot && (
          <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>
            {z.lineId ? <Wert z={z} feld="quantity" text={zahl} unsicher={unsicher} /> : z.quantity !== null ? `${mengeText(z.quantity)} ${z.unitCode ?? ''}` : ''}
          </td>
        )}
        {ansicht.prices && (
          <>
            <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>
              {nichtAngeboten ? '—' : <Wert z={z} feld="unitPrice" text={geld} unsicher={unsicher} />}
              {einheit && einheit !== z.unitCode && !nichtAngeboten && <span className="block text-xs text-muted-foreground">je {einheit}</span>}
              {/* Bestellung: was bestellt war, wenn der Lieferant etwas anderes nennt. */}
              {!angebot && z.ordered?.unitPrice != null && (z.read?.unitPrice ?? z.values?.unitPrice ?? null) !== null && (z.read?.unitPrice ?? z.values?.unitPrice) !== z.ordered.unitPrice && (
                <span className="block text-xs text-muted-foreground">bestellt {preisText(z.ordered.unitPrice)}</span>
              )}
            </td>
            {spalten.pe && (
              <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>
                {nichtAngeboten || ((z.read?.priceUnit ?? z.values?.priceUnit ?? 1) === 1 && (z.values?.priceUnit ?? 1) === 1) ? '' : <Wert z={z} feld="priceUnit" text={zahl} unsicher={unsicher} />}
              </td>
            )}
            {spalten.rabatt && <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>{nichtAngeboten ? '' : <Wert z={z} feld="discountPercent" text={zahl} unsicher={unsicher} />}</td>}
            {spalten.netto && <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>{nichtAngeboten ? '' : <Wert z={z} feld="netPrice" text={geld} unsicher={unsicher} />}</td>}
          </>
        )}
        <td className={`whitespace-nowrap ${kiTd} text-right tabular-nums`}>
          {angebot ? (
            <Wert z={z} feld="leadTimeDays" text={(x) => `${x} ${Number(x) === 1 ? 'Tag' : 'Tage'}`} unsicher={unsicher} />
          ) : (
            <>
              <Wert z={z} feld="deliveryAt" text={(x) => fmtDate(String(x))} unsicher={unsicher} />
              {z.ordered?.deliveryDate && <span className="block text-xs text-muted-foreground">Wunsch {fmtDate(z.ordered.deliveryDate)}</span>}
            </>
          )}
        </td>
        {/* Die Entscheidung je Zeile wie im Vorschlagsfenster: offen „Verwerfen", übernommen „übernommen", sonst nichts. */}
        <KiEntscheidungZelle entscheidung={z.read ? null : z.confirmedAt ? 'TAKEN' : null} darf={kann} disabled={laeuft} onVerwerfen={onVerwerfen} sonst={z.read || z.confirmedAt ? undefined : ''} />
      </tr>
    </>
  )
}
